// The check-in's consent prompt (UC-GOVERNANCE-017; BR-GOVERNANCE-027; PAT-74, Q-15), shared by Today and the calendar's panel:
// every appointment carries its patient's consent standing (`patientConsentStatus`, derived on the server from the latest
// record), and a patient with none on record or one on replaced wording is named so beside them. Checking such a patient in
// goes ahead — nothing waits for consent — and says so, with the two ways to record it: at the desk (the desk capture on the
// patient's Consents tab, UC-GOVERNANCE-013 A3) and on the kiosk (a `Consent` handover that returns here, UC-PATIENTS-022,
// asked first: BR-PATIENTS-041), each offered only to a role holding its permission. A `Current` or `Withdrawn` patient is not
// prompted.
import type { AppointmentView } from "@visionx/contracts";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { describeError } from "../api/problem";
import { ConfirmHandOver } from "../kiosk/ConfirmHandOver";
import { enterKiosk } from "../kiosk/mode";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Chip } from "../ui/bits";
import { deskCapturePath } from "./PatientsConsents";

type Prompted = "None" | "Outdated";
const WORD: Record<Prompted, string> = { None: "Consent missing", Outdated: "Consent outdated" };
const ASK: Record<Prompted, string> = { None: "Ask the patient to sign the consent.", Outdated: "Ask the patient to sign the current wording." };

/** The standing that asks for a signing, or null (`Current`, `Withdrawn`, or an older answer without the field). */
export function consentToAsk(a: Pick<AppointmentView, "patientConsentStatus">): Prompted | null {
  return a.patientConsentStatus === "None" || a.patientConsentStatus === "Outdated" ? a.patientConsentStatus : null;
}

/** "Consent missing" / "Consent outdated" beside the patient; nothing for any other standing. */
export function ConsentChip({ a }: { a: Pick<AppointmentView, "patientConsentStatus"> }) {
  const ask = consentToAsk(a);
  if (!ask) return null;
  return <span className="chips"><Chip tone="warn" dot>{WORD[ask]}</Chip></span>;
}

/**
 * The two ways to record it, for the roles that may: Record at the desk opens the patient's Consents tab on the desk capture;
 * Re-consent on kiosk asks first, then hands this device over on the consent step and comes back to `returnTo`.
 */
export function ConsentActions({ a, returnTo, onError }: { a: Pick<AppointmentView, "patientId" | "patientConsentStatus">; returnTo: string; onError: (n: ReactNode) => void }) {
  const s = useSessionView();
  const navigate = useNavigate();
  const block = useWriteBlock();
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  if (!consentToAsk(a)) return null;
  const desk = can(s, "governance.consent.write");
  const kiosk = can(s, "patients.registration.handover");
  if (!desk && !kiosk) return null;
  const handOver = async () => {
    if (!s) return;
    setBusy(true);
    try { await enterKiosk({ formType: "Consent", patientId: a.patientId, returnTo, session: s }); navigate("/kiosk"); }
    catch (err) { onError(<Alert tone="crit"><b>Could not start the kiosk.</b> {describeError(err)}</Alert>); }
    finally { setBusy(false); }
  };
  return (
    <>
      {desk && <Link className="btn sm" to={deskCapturePath(a.patientId)}>Record at the desk</Link>}
      {kiosk && <button className="btn sm" type="button" disabled={block !== null || busy} title={block ? WRITE_BLOCK_TITLE[block] : "Hand this device to the patient on the consent step"} onClick={() => setAsking(true)}>{busy ? "Starting…" : "Re-consent on kiosk"}</button>}
      {asking && <ConfirmHandOver onCancel={() => setAsking(false)} onHandOver={() => { setAsking(false); void handOver(); }} />}
    </>
  );
}

/** The notice after a check-in whose patient has to be asked (UC-GOVERNANCE-017 step 4); null when nothing is to be asked. */
export function consentArrivalNotice(a: Pick<AppointmentView, "patientId" | "patientConsentStatus" | "patient">, returnTo: string, onError: (n: ReactNode) => void): ReactNode | null {
  const ask = consentToAsk(a);
  if (!ask) return null;
  return (
    <Alert tone="warn" actions={<ConsentActions a={a} returnTo={returnTo} onError={onError} />}>
      <b>{a.patient.displayName} has arrived — {WORD[ask].toLowerCase()}.</b> {ASK[ask]}
    </Alert>
  );
}
